import React, { useState } from 'react'
import { Card, CardBody, CardFooter, Col, Label, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import CustomToast from '../../../utils/CustomToast';
import DownloadIcon from 'assets/img/bis/download-icon.png';
import axios from 'axios';
import { AiFillInfoCircle } from 'react-icons/ai';
import useSiteCookies from '../useSiteCookies';
import { uploadFileInChunks, CHUNK_SIZE } from 'utils/chunkedUpload';

// Chunk size and concurrency live in utils/chunkedUpload so every uploader in
// the app stays in step. These two are local because the utility does not own
// the axios calls.
const CHUNK_TIMEOUT = 300000;        // 5 min per chunk; must cover CHUNK_SIZE on a slow line
const COMBINE_TIMEOUT = 300000;      // 5 min for the combine call
const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20MB; frontend-only cap, images only

const FileUploaderModal = ({ show, onHide, instance, id, onData }) => {
    const instanceType = instance.instance;
    const instanceId = instance.id;
    const instancePDFType = instance.type || '';

    const { currentUser, token } = useSiteCookies();

    const [formStatus, setFormStatus] = useState(false);
    const [uploadedFileError, setUploadedFileError] = useState(false);
    const [uploadedFile, setUploadedFile] = useState('Upload file here');

    const [documentFormDataId, setDocumentFormDataId] = useState(0);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [item, setItem] = useState({ name: '', value: '' });

    const allowedFileTypes = {
        image: ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'],
        video: ['video/mp4', 'video/quicktime', 'video/x-msvideo'], // quicktime = mov, x-msvideo = avi
        audio: ['audio/mpeg', 'audio/wav'], // mpeg = mp3
        document: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv'],
        pdf: ['application/pdf']
    };

    const uploadInstanceLabel = {
        document: 'DOCUMENT',
        pdf: 'DOCUMENT',
        photo_1: 'PHOTO 1',
        photo_2: 'PHOTO 2',
        image: 'IMAGE',
        video: 'VIDEO',
        audio: 'AUDIO',
    };

    const hiddenFileInputPdf = React.useRef(null);

    const postFile = async (data) => {
        return await axios.post(
            process.env.REACT_APP_API_ENDPOINT + `category/question/upload`, data, {
            params: {
                user_id: currentUser,
                token: token
            }
        });
    }

    const postChunk = async (data, onUploadProgress) => {
        return await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'store/entry/file/chunk',
            data,
            {
            params: { user_id: currentUser, token: token },
            timeout: CHUNK_TIMEOUT,
            onUploadProgress,
            }
        );
    };

    const postCombineChunk = async (data) => {
        return await axios.post(
            process.env.REACT_APP_API_ENDPOINT + 'combine/entry/file/chunk',
            data,
            {
            params: { user_id: currentUser, token: token },
            timeout: COMBINE_TIMEOUT,
            }
        );
    };


    const submitDocument = async (file, filename, count, onUploadProgress) => {
        const dataArray = new FormData();
        dataArray.append("file", file);
        dataArray.append("filename", filename);
        dataArray.append("index", count);

        try {
            return await postChunk(dataArray, onUploadProgress);
        } catch (error) {
            throw error; // fail fast, will be caught in validateFile
        }
    };


    const uploadChunkWithRetry = async (chunk, fileName, chunkIndex, onUploadProgress, maxRetries = 2) => {
        let lastError;
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const response = await submitDocument(chunk, fileName, chunkIndex, onUploadProgress);
                if (response.data.status !== 'Success') {
                    throw new Error(
                        response.data.errors ? JSON.stringify(response.data.errors) : 'Chunk upload failed'
                    );
                }
                return response;
            } catch (error) {
                lastError = error;
                if (attempt < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
                }
            }
        }
        throw lastError;
    };

    const combineDocument = async (filename, extension, totalChunks) => {
        try {
            const data = { filename, extension, total_chunks: totalChunks };
            const response = await postCombineChunk(data);

            if (response.data.status === "Success") {
                setFormStatus(false);
                setUploadedFile(filename);

                const fileUrl = response.data.data.url;
                if (fileUrl) {
                    setItem({ name: "File", value: fileUrl });
                }
            } else {
                setUploadedFile("Upload file here");
                CustomToast.warning("Upload failed. Please check your internet connection and try again.");
                setFormStatus(false);
                CustomToast.defaultError();
                // throw new Error("Combine failed");
            }
        } catch (error) {
            // setUploadedFileError(true);
            setUploadedFile("Upload file here");
            CustomToast.warning("Upload failed. Please check your internet connection and try again.");
            setFormStatus(false);
            CustomToast.defaultError();
        }
    };


    const handleClickFile = () => {
        hiddenFileInputPdf.current.click();
    };

    const handleDragEnter = e => {
        e.preventDefault();
    };

    const handleDragOver = e => {
        e.preventDefault();
    };

    const handleDragLeave = e => {
        e.preventDefault();
    };

    const handleDrop = e => {
        e.preventDefault();
    
        const droppedFiles = e.dataTransfer.files;
        const totalFilesCount = droppedFiles.length;
    
        if (totalFilesCount > 1) {
            const message = "This field accepts only 1!";
            CustomToast.warning(message);
            return;
        }
    
        if (formStatus)
            return;
    
        setFormStatus(true);
    
        if (droppedFiles[0]) {
            const file = droppedFiles[0];
            const size = file.size;
        
            if (instanceType === 'image') {
                imageValidations(file);
            } else if (instanceType === 'video') {
                videoValidations(file);
            } else if (instanceType === 'audio') {
                audioValidations(file);
            } else if (instancePDFType === 'pdf') {
                documentPDFValidations(file);
            } else if (instanceType === 'document') {
                documentValidations(file);
            }
        }
    };

    const handleChangeFiles = ({ target }) => {
        if (target.files.length < 1 || !target.validity.valid) {
            return;
        }

        setFormStatus(true);

        const file = target.files[0];

        if (instanceType === "image") {
            validateFile(file, "image");
        } else if (instanceType === "video") {
            validateFile(file, "video");
        } else if (instanceType === "audio") {
            validateFile(file, "audio");
        } else if (instancePDFType === "pdf") {
            validateFile(file, "pdf");
        } else if (instanceType === "document") {
            validateFile(file, "document");
        }

        target.value = "";
    };


    const validateFile = async (file, type) => {
        if (!allowedFileTypes[type].includes(file.type)) {
            setFormStatus(false);
            CustomToast.warning(`Please upload a valid ${type} file!`);
            return;
        }

        if (type === 'image' && file.size > MAX_IMAGE_SIZE) {
            setFormStatus(false);
            CustomToast.warning('Image must be 20MB or smaller.');
            return;
        }

        setUploadedFile("Uploading file...");
        setUploadedFileError(false);
        setUploadProgress(0);

        const fileName = new Date().getTime().toString() + currentUser.toString();
        const extension = file.name.split(".").pop();
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        try {
            await uploadFileInChunks({
                file,
                chunkSize: CHUNK_SIZE,
                uploadChunk: (chunk, index, onProgress) =>
                    uploadChunkWithRetry(chunk, fileName, index, onProgress),
                onProgress: setUploadProgress,
            });
        } catch (error) {
            console.error('Chunk upload failed after retries', error);
            setFormStatus(false);
            setUploadedFile("Upload file here");
            setUploadProgress(0);
            CustomToast.warning("Upload failed. Please check your internet connection and try again.");
            return;
        }

        try {
            await combineDocument(fileName, extension, totalChunks);
            setUploadProgress(0);
            setUploadedFileError(false);
        } catch (error) {
            setFormStatus(false);
            setUploadedFile("Upload file here");
            setUploadProgress(0);
            CustomToast.defaultError();
        }
    };
    
    const imageValidations = file => validateFile(file, 'image');
    const videoValidations = file => validateFile(file, 'video');
    const audioValidations = file => validateFile(file, 'audio');
    const documentValidations = file => validateFile(file, 'document');
    const documentPDFValidations = file => validateFile(file, 'pdf');

    const submitFile = (file, fileName) => {
        setFormStatus(true);
        const dataArray = new FormData();
        dataArray.append('file', file);
        
        postFile(dataArray).then(response => {
            const status = response.data.status;
            if (status === 'Success') {
                setFormStatus(false);
                setUploadedFile(fileName);

                const data = response.data.data.url;
                if (data) {
                    setItem({ name: 'File', value: data });
                }
            } else {
                CustomToast.defaultError();
                setFormStatus(false);
            }
        }).catch(() => {  
            CustomToast.defaultError();
            setFormStatus(false);
        });
    }

    const handleUpload = () => {
        if (item.value === '') {
            setUploadedFileError(true);
            setFormStatus(false);
            return;
        } else {
            onData({ target: item });
            onHide();
            setUploadedFile('Upload file here');
        }
    }

    const toggleHide = () => {
        onHide();
        setItem({ name: '', value: '' });
        setUploadedFile('Upload file here');
        setUploadedFileError(false);
    }

    return (
        <>
            <Modal
                isOpen={show}
                className="modal-preview"
                fade={false}
                centered
                id="edit-judge-modal"
            >
            <ModalHeader className="text-left">
                UPLOAD {uploadInstanceLabel[instanceType]}
                {instanceType === 'image' && (
                    <div className="bis-tooltip p-0 ml-2" style={{ border: 'none', background: 'transparent', verticalAlign: 'middle' }}>
                        <AiFillInfoCircle />
                        <span className="bis-tooltiptext" style={{ width: '220px', marginLeft: '-110px', textTransform: 'none' }}>Maximum image size is 20MB</span>
                    </div>
                )}
                {!formStatus && (
                <button
                    type="button"
                    className="close react-modal-close"
                    data-dismiss="modal"
                    aria-label="Close"
                    onClick={() => toggleHide()}
                >
                    <span aria-hidden="true">&times;</span>
                </button>
                )}
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody className="card_container">
                                <Label
                                    onClick={handleClickFile}
                                    onDragEnter={handleDragEnter}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    className={`${uploadedFileError
                                        ? 'download_icon form-invalid'
                                        : 'download_icon'
                                        }`}
                                    style={
                                        formStatus
                                        ? { cursor: 'not-allowed', opacity: '0.5' }
                                        : {}
                                    }
                                >
                                    <img src={DownloadIcon} draggable={false}/>
                                    <br />
                                    {formStatus ? `Uploading... ${uploadProgress}%` : uploadedFile}
                                </Label>
                                {uploadedFileError ? (
                                    <p role="alert" className="invalid-feedback">
                                        File is required
                                    </p>
                                ) : null}
                                {formStatus && (
                                    <div className="mt-2">
                                        <div style={{ height: 8, width: '100%', backgroundColor: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}>
                                            <div
                                                style={{
                                                    height: '100%',
                                                    width: uploadProgress + '%',
                                                    backgroundColor: '#0d6efd',
                                                    transition: 'width 0.2s ease'
                                                }}
                                            />
                                        </div>
                                        <small className="text-muted">{uploadProgress}%</small>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    ref={hiddenFileInputPdf}
                                    accept={
                                        instanceType === 'image'
                                        // ? '.jpg, .jpeg, .png, .gif, .bmp, .webp, .svg'
                                        ? '.jpeg, .png, .jpg, .gif'
                                        : instanceType === 'video'
                                        // ? '.mp4, .avi, .mov, .wmv, .flv, .mkv, .webm'
                                        ? '.mp4, .mov, .avi'
                                        : instanceType === 'audio'
                                        // ? '.mp3, .wav, .ogg, .aac, .flac, .m4a'
                                        ? '.mp3, .wav'
                                        : instancePDFType === 'pdf'
                                        ? '.pdf'
                                        : instanceType === 'document'
                                        // ? '.pdf, .doc, .docx, .odt, .rtf, .txt, .xls, .xlsx, .csv, .ods, .ppt, .pptx, .odp, .zip, .rar, .7z, .tar, .gz,  .html, .htm, .css, .js, .json, .xml, .php, .py, .java, .c, .cpp'
                                        ? '.pdf, .doc, .docx, .xlsx, .csv'
                                        : 'image/*'
                                    }
                                    onChange={handleChangeFiles}
                                    style={{ display: 'none' }}
                                    required
                                    disabled={formStatus}
                                />
                            </CardBody>
                            <CardFooter className="text-right">
                                {!formStatus && (
                                <button
                                    type="button"
                                    className="btn btn-main btn-main-alt mr-2"
                                    data-dismiss="modal"
                                    onClick={() => toggleHide()}
                                >
                                    CLOSE
                                </button>
                                )}
                                {!formStatus ? (
                                <button
                                    type="button"
                                    className="newbuttontheme w-25"
                                    onClick={handleUpload}
                                >
                                    SAVE
                                </button>
                                ) : (
                                <button
                                    type="button"
                                    className="newbuttontheme w-25"
                                >
                                    UPLOADING...
                                </button>
                                )}
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </ModalBody>
            </Modal>
        </>
    )
}

export default FileUploaderModal
